import Resource, { createResourceHook } from '@tabeo/resync'
import api from '@tabeo/sharpei/utils/api'
import { User, UserAccount } from '@tabeo/ts-types'

interface Params {
  limit?: number
  userID?: number
}

type NormalizedUser = UserAccount & User

class AdminUsers extends Resource<NormalizedUser[], Params> {
  getInitialConfig() {
    return {
      refreshInterval: 5 * 60 * 1000,
    }
  }

  mutation: null | Promise<void> = null

  async fetch() {
    if (this.mutation) {
      await this.mutation
    }

    const { limit = 150, userID } = this.params

    const conditions = userID ? { userID } : null
    const jsonConditions = conditions
      ? `&jsonConditions=${JSON.stringify(conditions)}`
      : ''

    const { admins } = await api.get<{
      admins: (UserAccount & {
        user: User
      })[]
    }>(
      `/admins?subResource=user&limit=${limit}&subResource=stateMachineTransitions&order=firstName:asc${jsonConditions}`
    )

    const normalized = admins.map(u => ({
      ...u,
      ...(u.user && u.user),
    }))
    this.data = normalized

    return normalized
  }

  createUser = async (data: Partial<UserAccount>) => {
    const response = await api.post(`/admins`, data)
    await this.fetch()
    return response
  }

  updateUserById = async ({ id, ...data }: { id: number }) => {
    const response = await api.put(`/admins/${id}`, data)
    await this.fetch()
    return response
  }

  blockUserById = async ({ id }: { id: number }) => {
    if (!this.data) {
      throw new Error('No data')
    }
    this.mutation = api.put(`/admins/${id}/block`, {})
    this.data = this.data.map(u => ({
      ...u,
      status: u.id === id ? 'blocked' : u.status,
    }))
    const response = await this.mutation
    this.mutation = null
    await this.fetch()
    return response
  }

  unblockUserById = async ({ id }: { id: number }) => {
    if (!this.data) {
      throw new Error('No data')
    }
    this.mutation = api.put(`/admins/${id}/unblock`, {})
    this.data = this.data.map(u => ({
      ...u,
      status: u.id === id ? 'activated' : u.status,
    }))
    const response = await this.mutation
    this.mutation = null
    await this.fetch()
    return response
  }

  removeUserById = async ({ id }: { id: number }) => {
    const response = await api.delete(`/admins/${id}`, {})
    await this.fetch()
    return response
  }
}

export default AdminUsers
export const useAdminUsers = createResourceHook(AdminUsers)
